/* GrowthOS — Sales Order form sections (used by addSales.jsx for Create/Edit).
   Modeled on regularQuoteForm.jsx (QuoteForm design) mapped onto the real Sales
   Order data model + APIs. Reuses the shared gos-cl-* / gos-dd-* form primitives
   + the gos-qf-* Address grid and Items table (div-based, not a <table>) with a
   live Summary. Line-item math + totals live in the container (calculateQuoteTotals
   via handleChangeForm(patch, rowIndex, 'SalesOrder')); this file only renders +
   forwards patches. Replaces the legacy ChildSalesOrder AG-grid on this page only. */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getActiveOwners,
  getGenericMasterFilteredListByKey,
  getProductList,
  getQuoteList,
} from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import PaginatedDropdown from '../../shared/component/PaginatedDropdown';
import apiServices from '../../services/service';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { productInputData } from '../../shared/constants';

const ICONS = {
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  link: <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />,
  location: (<><path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>),
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
};
const FIc = ({ name, size = 16, style, className }) => (
  <svg className={className} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {ICONS[name] || null}
  </svg>
);

const Select = ({ value, placeholder, options, onChange, disabled, searchable, onSearch, onScroll, loading, small }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const onListScroll = (e) => {
    if (!onScroll) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onScroll();
  };
  return (
    <div className={'gos-dd' + (small ? ' gos-dd-sm' : '')} ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" onChange={(e) => onSearch && onSearch(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list" onScroll={onScroll ? onListScroll : undefined}>
            {loading && options.length === 0 ? <div className="gos-dd-empty">Loading…</div> : null}
            {!loading && options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {loading && options.length > 0 ? <div className="gos-dd-empty">Loading more…</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';
const money = (n) => `${CURRENCY} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SalesForm = ({
  formData,
  handleChangeForm,
  handleChange,
  errormsg,
  handleAddRow,
  handleDeleteRow,
  handleSameAsBillingChange,
  handleChangeCheckBox,
}) => {
  const owner = useSelector(getActiveOwners);
  const saleOrderStatus = useSelector(getGenericMasterFilteredListByKey('SaleOrderStatus'));
  const carrier = useSelector(getGenericMasterFilteredListByKey('Carrier'));
  const currency = useSelector(getGenericMasterFilteredListByKey('Currency'));
  const quoteListData = useSelector(getQuoteList);
  const productList = useSelector(getProductList);
  useFetchIfNeeded(getProductList, fetchDataAndUpdateStore, { key: 'productList', inputData: productInputData });

  const {
    options: accountOptions,
    isLoading: isAccountLoading,
    handleSearch: onAccountSearch,
    handleScroll: onAccountScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getAccountsDropdown, responseKey: 'accountList' });
  const {
    options: opportunityOptions,
    isLoading: isOpportunityLoading,
    handleSearch: onOpportunitySearch,
    handleScroll: onOpportunityScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getOpportunityDropdown, responseKey: 'opportunityList' });
  const {
    options: contactOptions,
    isLoading: isContactLoading,
    handleSearch: onContactSearch,
    handleScroll: onContactScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getContactsDropdown, responseKey: 'contactList' });

  const opt = (list, vKey, lKey, fmt) => (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));
  const productItems = opt(productList, 'id', 'name');

  const rows = formData?.saleOrderDetails || [];

  // ---- generic field renderer (text | number | select | select-search | date | checkbox | textarea) ----
  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select' || fl.type === 'select-search') {
      control = (
        <Select
          value={fl.value !== undefined ? fl.value : formData?.[fl.display] || ''}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          searchable={fl.type === 'select-search'}
          onSearch={fl.onSearch}
          onScroll={fl.onScroll}
          loading={fl.loading}
        />
      );
    } else if (fl.type === 'date') {
      control = (
        <input
          type="date"
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          value={formData?.[fl.name] ? String(formData[fl.name]).substring(0, 10) : ''}
          onChange={handleChangeForm}
        />
      );
    } else if (fl.type === 'number') {
      control = (
        <div className="gos-qf-money">
          <span className="gos-qf-money-sym">{CURRENCY}</span>
          <input
            className={'gos-cl-input' + (err ? ' err' : '')}
            type="number"
            min={0}
            step="0.01"
            name={fl.name}
            placeholder="0.00"
            value={formData?.[fl.name] ?? ''}
            onChange={handleChangeForm}
          />
        </div>
      );
    } else if (fl.type === 'checkbox') {
      const on = !!formData?.[fl.name];
      control = (
        <button
          type="button"
          className={'gos-qf-check' + (on ? ' on' : '')}
          onClick={() => handleChangeCheckBox({ target: { name: fl.name, checked: !on } })}
        >
          <span className={'lead-check sm' + (on ? ' on' : '')}>{on ? <FIc name="check" size={11} /> : null}</span>
          {fl.checkLabel || fl.label}
        </button>
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name}>
        {fl.type === 'checkbox' ? null : (
          <label className="gos-cl-label">
            {fl.label}
            {fl.req ? <span className="gos-cl-req">*</span> : null}
          </label>
        )}
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const sectionCard = (icon, title, sub, right, body) => (
    <div className="gos-lr-card gos-cl-sec">
      <div className="gos-cl-sec-head">
        <span className="gos-cl-sec-ic"><FIc name={icon} size={17} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-sec-title">{title}</div>
          <div className="gos-cl-sec-sub">{sub}</div>
        </div>
        {right || null}
      </div>
      {body}
    </div>
  );

  const infoFields = [
    { label: 'Sales Owner', name: 'salesOwnerId', type: 'select', display: 'salesOwner', req: true, options: opt(owner, 'userId', 'display_name', NameFormatter) },
    { label: 'Subject', name: 'subject', req: true },
    { label: 'Purchase Order', name: 'purchaseOrder' },
    { label: 'Customer No.', name: 'customerNo' },
    { label: 'Order Date', name: 'orderDate', type: 'date', req: true },
    { label: 'Due Date', name: 'dueDate', type: 'date', req: true },
    { label: 'Carrier', name: 'carrier', type: 'select', display: 'carrier', options: opt(carrier, 'id', 'displayName') },
    { label: 'Status', name: 'status', type: 'select', display: 'status', options: opt(saleOrderStatus, 'id', 'displayName') },
    { label: 'Currency', name: 'currency', type: 'select', display: 'currency', options: opt(currency, 'id', 'displayName') },
    { label: 'Sales Commission', name: 'salesCommission', type: 'number' },
    { label: 'Excise Duty', name: 'exciseDuty', type: 'number' },
    { label: 'Pending', name: 'pending', type: 'checkbox', checkLabel: 'Mark as pending' },
  ];

  const assocFields = [
    { label: 'Account Name', name: 'accountName', req: true, type: 'select-search', display: 'accountName', options: opt(accountOptions, 'id', 'name'), onSearch: onAccountSearch, onScroll: onAccountScroll, loading: isAccountLoading },
    { label: 'Opportunity Name', name: 'opportunityName', type: 'select-search', display: 'opportunityName', options: opt(opportunityOptions, 'id', 'name'), onSearch: onOpportunitySearch, onScroll: onOpportunityScroll, loading: isOpportunityLoading },
    { label: 'Quote Name', name: 'quoteName', type: 'select', display: 'quoteName', options: opt(quoteListData, 'id', 'subject') },
    { label: 'Contact Name', name: 'contactName', type: 'select-search', display: 'contactName', options: opt(contactOptions, 'id', 'name'), onSearch: onContactSearch, onScroll: onContactScroll, loading: isContactLoading },
  ];

  const additionalFields = [
    { label: 'Terms and Conditions', name: 'termsAndConditions', type: 'textarea', full: true },
    { label: 'Description', name: 'descriptionInformation', type: 'textarea', full: true },
  ];

  // ---- Address (billing / shipping) ----
  const addrField = (name, label, full) => {
    const err = errormsg?.[name];
    return (
      <div className={'gos-cl-field' + (full ? ' gos-cl-field-full' : '')} key={name}>
        <label className="gos-cl-label">{label}</label>
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={name}
          placeholder={'Enter ' + label.replace(/^(Billing|Shipping) /, '')}
          value={formData?.[name] ?? ''}
          onChange={handleChangeForm}
        />
      </div>
    );
  };
  const billingEmpty = !formData?.billingStreet && !formData?.billingCity && !formData?.billingState && !formData?.billingCountry && !formData?.billingCode;
  const sameAddr = !!formData?.sameAsBilling;

  const addressBody = (
    <div className="gos-qf-addr">
      <div className="gos-qf-addr-col">
        <div className="gos-qf-addr-title">Billing Address</div>
        <div className="gos-qf-addr-fields">
          {addrField('billingStreet', 'Billing Street', true)}
          {addrField('billingCity', 'Billing City')}
          {addrField('billingState', 'Billing State')}
          {addrField('billingCountry', 'Billing Country')}
          {addrField('billingCode', 'Billing Code')}
        </div>
      </div>
      <div className="gos-qf-addr-col">
        <div className="gos-qf-addr-title spread">
          <span>Shipping Address</span>
          <button
            type="button"
            className={'gos-qf-check' + (sameAddr ? ' on' : '')}
            onClick={() => handleSameAsBillingChange({ target: { checked: !sameAddr } })}
            disabled={billingEmpty}
          >
            <span className={'lead-check sm' + (sameAddr ? ' on' : '')}>
              {sameAddr ? <FIc name="check" size={11} /> : null}
            </span>
            Same as Billing Address
          </button>
        </div>
        <div className={'gos-qf-addr-fields' + (sameAddr ? ' is-disabled' : '')}>
          {addrField('shippingStreet', 'Shipping Street', true)}
          {addrField('shippingCity', 'Shipping City')}
          {addrField('shippingState', 'Shipping State')}
          {addrField('shippingCountry', 'Shipping Country')}
          {addrField('shippingCode', 'Shipping Code')}
        </div>
      </div>
    </div>
  );

  // ---- Items table + summary ----
  const itemsError = errormsg?.['saleOrderDetails[0].productId'];
  const HEADS = ['#', 'Product', 'Description', 'Qty', 'List Price', 'Amount', 'Disc %', 'Discount', 'Tax %', 'Tax', 'Total', ''];
  const NUM_HEADS = ['Qty', 'List Price', 'Amount', 'Disc %', 'Discount', 'Tax %', 'Tax', 'Total'];

  const itemsBody = (
    <div className="gos-qf-items">
      <div className="gos-qf-items-main">
        {itemsError ? <div className="gos-qf-items-err gos-cl-err">{itemsError}</div> : null}
        <div className="gos-qf-items-scroll">
          <div className="gos-qf-grid">
            <div className="gos-qf-hrow">
              {HEADS.map((h, i) => <div key={i} className={'gos-qf-hcell' + (NUM_HEADS.includes(h) ? ' gos-num' : '')}>{h}</div>)}
            </div>
            {rows.map((r, idx) => (
              <div className="gos-qf-row" key={r.id || idx}>
                <div className="gos-qf-c gos-qf-sn">{idx + 1}</div>
                <div className="gos-qf-c gos-qf-prod">
                  <Select
                    small
                    value={r.productName || ''}
                    placeholder="Select Product"
                    options={productItems}
                    onChange={(o) => handleChange(o, 'productId', idx)}
                  />
                </div>
                <div className="gos-qf-c">
                  <input className="gos-qf-cell" value={r.description ?? ''} placeholder="—" onChange={(e) => handleChangeForm({ description: e.target.value }, idx, 'SalesOrder')} />
                </div>
                <div className="gos-qf-c gos-num">
                  <input className="gos-qf-cell gos-num" type="number" min={0} value={r.quantity ?? ''} onChange={(e) => handleChangeForm({ quantity: e.target.value }, idx, 'SalesOrder')} />
                </div>
                <div className="gos-qf-c gos-num">
                  <input className="gos-qf-cell gos-num" type="number" min={0} step="0.01" value={r.listPrice ?? ''} onChange={(e) => handleChangeForm({ listPrice: e.target.value }, idx, 'SalesOrder')} />
                </div>
                <div className="gos-qf-c gos-num gos-qf-ro">{money(r.amount)}</div>
                <div className="gos-qf-c gos-num">
                  <input className="gos-qf-cell gos-num" type="number" min={0} max={100} step="0.01" value={r.discountPercent ?? ''} onChange={(e) => handleChangeForm({ discountPercent: e.target.value, discountAmount: 0 }, idx, 'SalesOrder')} />
                </div>
                <div className="gos-qf-c gos-num gos-qf-ro">{money(r.discountAmount)}</div>
                <div className="gos-qf-c gos-num">
                  <input className="gos-qf-cell gos-num" type="number" min={0} max={100} step="0.01" value={r.taxPercent ?? ''} onChange={(e) => handleChangeForm({ taxPercent: e.target.value, taxAmount: 0 }, idx, 'SalesOrder')} />
                </div>
                <div className="gos-qf-c gos-num gos-qf-ro">{money(r.taxAmount)}</div>
                <div className="gos-qf-c gos-num gos-qf-total">{money(r.lineTotal)}</div>
                <div className="gos-qf-c gos-qf-delcell">
                  <button type="button" className="gos-qf-del" title="Remove item" onClick={() => handleDeleteRow(idx, r.id)} disabled={rows.length <= 1}>
                    <FIc name="trash" size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="gos-qf-summary">
        <div className="gos-qf-summary-head">Order Summary</div>
        <div className="gos-qf-summary-body">
          <div className="gos-qf-sum-row"><span>Sub Total</span><span className="gos-qf-sum-val">{money(formData?.subTotal)}</span></div>
          <div className="gos-qf-sum-row"><span>Discount</span><span className="gos-qf-sum-val">− {money(formData?.discountTotal)}</span></div>
          <div className="gos-qf-sum-row"><span>Tax</span><span className="gos-qf-sum-val">+ {money(formData?.taxTotal)}</span></div>
          <div className="gos-qf-sum-row">
            <span>Adjustment</span>
            <input className="gos-qf-adjust" type="number" step="0.01" value={formData?.adjustment ?? ''} onChange={(e) => handleChangeForm({ target: { name: 'adjustment', value: e.target.value } })} />
          </div>
          <div className="gos-qf-sum-row gos-qf-grand"><span>Grand Total</span><span className="gos-qf-sum-val">{money(formData?.grandTotal)}</span></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="gos-qf-body">
      {sectionCard('note', 'Sales Order Information', 'Ownership, dates & amounts', null,
        <div className="gos-cl-sec-body">{infoFields.map(renderField)}</div>)}
      {sectionCard('link', 'Association With', 'Link this order to a record', null,
        <div className="gos-cl-sec-body">{assocFields.map(renderField)}</div>)}
      {sectionCard('location', 'Address Information', 'Billing & shipping location', null, addressBody)}
      {sectionCard('grid', <>Items <span className="gos-cl-req">*</span></>, 'Products & pricing',
        <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={handleAddRow}>
          <FIc name="plus" size={15} /> Add Item
        </button>,
        itemsBody)}
      {sectionCard('template', 'Additional Details', 'Terms & description', null,
        <div className="gos-cl-sec-body">{additionalFields.map(renderField)}</div>)}
    </div>
  );
};

export default SalesForm;
